import { clamp, EncounterKind, initialState, step, type RunnerState } from '@runner/simulation';
import type { QuoteView, TradeRequest, WorldActions, WorldView } from '../types';
import type { Aim, SteeringConfig, SteeringPlan } from './types';
const abs = (v: bigint) => v < 0n ? -v : v;
export function steeringReady(v: WorldView): boolean {
  return v.phase === 'running' && v.stage === 'input-open' && !!v.run && !!v.openInput &&
    v.openInput.window === v.timing.nextStepWindow && v.openInput.window === v.timing.currentWindow &&
    !v.freshness.stale && !v.freshness.catchingUp && !(v.freshness.pendingSteps ?? 0) &&
    (v.mode === 'demo' || (v.freshness.api === 'ok' && v.freshness.lastOkAt !== null && Date.now() - v.freshness.lastOkAt < 10_000));
}
export function aimX(v: WorldView, aim: Aim, config: SteeringConfig): bigint {
  const end = v.steps.at(-1)?.state ?? initialState();
  if (aim.kind === 'shoe') {
    const shoe = v.run?.course.find(o => o.kind === EncounterKind.GOLDEN_SHOE && o.funded && o.y > end.y);
    if (!shoe) throw new Error('No funded shoe ahead. Choose a lane.');
    return (shoe.xL + shoe.xR) / 2n;
  }
  return clamp(BigInt(Math.round(aim.kind === 'left' ? -800 : aim.kind === 'right' ? 800 : aim.x ?? 0)), -config.movement.xMax, config.movement.xMax);
}
/** Uses pinned engine parameters, current aggregate input, momentum and volume. Never equates volume with direction. */
export function steeringIntent(v: WorldView, aim: Aim, config: SteeringConfig) {
  if (!steeringReady(v)) throw new Error('Waiting for a fresh, open steering window.');
  if (config.version !== v.run!.configVersion || config.engineVersion !== v.run!.engineVersion || ![1, 2].includes(config.engineVersion)) throw new Error('Verified movement rules are not available for this run.');
  const from = v.steps.at(-1)?.state ?? initialState();
  const target = aimX(v, aim, config);
  const input = v.openInput!;
  const cur = v.run!.course.findIndex(o => o.y > from.y);
  const trajectory = (delta: bigint): RunnerState[] => {
    const path: RunnerState[] = []; let state = from; let cursor = cur < 0 ? v.run!.course.length : cur;
    for (let i = 0; i < 3; i++) {
      const next = step(state, v.run!.course, cursor, i === 0 ? input.net + delta : 0n, i === 0 ? input.volume + abs(delta) : 0n, v.refSupply, config.movement, config.handlers, config.engineVersion);
      path.push(next.state); state = next.state; cursor = next.cursor;
      if (state.y >= BigInt(v.run!.courseLength)) break;
    }
    return path;
  };
  const project = (delta: bigint) => trajectory(delta)[0];
  const baselinePath = trajectory(0n), baseline = baselinePath[0];
  let best = 0n, score = abs(baselinePath.at(-1)!.x - target);
  // Include no-trade and both saturated extremes. Ties choose less turnover, including zero when momentum suffices.
  for (let i = -60; i <= 60; i++) {
    const u = BigInt(i) * config.movement.uMax / 60n;
    const net = u * v.refSupply / (1_000_000n * config.movement.sensitivity);
    const delta = net - input.net;
    const d = abs(trajectory(delta).at(-1)!.x - target);
    if (d < score || (d === score && abs(delta) < abs(best))) { best = delta; score = d; }
  }
  return { from, baseline, baselinePath, target, delta: best, project, trajectory };
}
/** Quotes stay exact-input and within the player's cap. Buy sizing searches actual pool quotes, never display price. */
export async function planSteering(v: WorldView, aim: Aim, config: SteeringConfig, caps: { buy: bigint; sell: bigint }, source: Pick<WorldActions, 'quote'>): Promise<SteeringPlan> {
  const intent = steeringIntent(v, aim, config);
  const base = { runId: v.run!.id, window: v.openInput!.window, targetX: Number(intent.target), from: intent.from, baseline: intent.baseline, createdAt: Date.now(), baselinePath: intent.baselinePath, path: intent.baselinePath };
  if (intent.delta === 0n) return { ...base, predicted: intent.baseline, req: null, quote: null, limited: false, message: 'The crowd and momentum already provide the best projected move. No trade needed.' };
  const side = intent.delta > 0n ? 'buy' : 'sell';
  const budget = side === 'buy' ? caps.buy : caps.sell;
  if (budget <= 0n) return { ...base, predicted: intent.baseline, req: null, quote: null, limited: true, message: `No ${side === 'buy' ? v.quote.symbol : 'RUNNER'} available within your play limit.` };
  const wanted = abs(intent.delta);
  let amount = side === 'sell' ? (wanted < budget ? wanted : budget) : budget;
  let q: QuoteView = await source.quote({ side, amountIn: amount });
  if (side === 'buy' && q.amountOut > wanted) {
    let lo = 0n, hi = budget;
    // A bounded binary search over on-chain pool quotes; at most nine reads per deliberate target selection.
    for (let i = 0; i < 8 && hi - lo > 1n; i++) {
      const mid = (lo + hi) / 2n;
      const candidate = await source.quote({ side, amountIn: mid });
      if (candidate.amountOut <= wanted) { lo = mid; q = candidate; amount = mid; } else hi = mid;
    }
    if (lo === 0n) return { ...base, predicted: intent.baseline, req: null, quote: null, limited: false, message: 'The useful adjustment is below the minimum trade size. No trade needed.' };
  }
  if (q.side !== side || q.amountIn !== amount || q.amountOut <= 0n || q.minOut <= 0n || q.slippageBps > 100 || q.priceImpactBps > 200) throw new Error('This trade exceeds the 1% slippage or 2% price-impact limit. Try a smaller play limit.');
  const delta = side === 'buy' ? q.amountOut : -amount;
  const predicted = intent.project(delta);
  if (abs(intent.trajectory(delta).at(-1)!.x - intent.target) >= abs(intent.baselinePath.at(-1)!.x - intent.target)) return { ...base, predicted: intent.baseline, req: null, quote: null, limited: true, message: 'A trade within this limit would not improve the projected path. No trade needed.' };
  const req: TradeRequest = { side, amountIn: amount, steeringWindow: v.openInput!.window, validUntil: Math.min(q.expiresAt, v.timing.windowEndsAt - 1) };
  return { ...base, predicted, path: intent.trajectory(delta), req, quote: q, limited: abs(delta) < wanted * 99n / 100n, message: 'Estimated next step. Other traders can change the outcome.' };
}
